import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";
import { BOOKING_URL } from "@/lib/booking";
import { BrandLogo } from "@/components/BrandLogo";

const LINKS = [
  ["Home", "/", "nav-home"], ["Real Estate Media", "/services", "nav-real-estate"], ["Web Design", "/web-design", "nav-web-design"],
  ["Weddings & Events", "/weddings-events", "nav-weddings-events"], ["Portfolio", "/work", "nav-work"], ["Contact", "/contact", "nav-contact"],
];

export function Navbar({ overlay = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const transparent = overlay && !scrolled && !open;
  const dashHref = user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/login";
  const text = "text-white";
  const linkCls = "text-[11px] uppercase tracking-[0.18em] link-underline transition-colors duration-300 text-white/70 hover:text-white";
  const goHome = () => window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

  return (
    <header data-testid="site-header" className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${transparent ? "bg-transparent" : "bg-[#0B0C0E]/85 backdrop-blur-xl border-b border-white/10"}`}>
      <nav className="px-6 sm:px-10 lg:px-16 h-24 flex items-center justify-between">
        <Link to="/" onClick={goHome} data-testid="nav-logo" aria-label="Mandalay Real Estate Media home" className="block shrink-0 h-14 w-[164px] sm:h-16 sm:w-[190px]">
          <BrandLogo location="header" />
        </Link>

        <div className="hidden xl:flex items-center gap-5">
          {LINKS.map(([l, to, id]) => <Link key={to} to={to} data-testid={id} className={linkCls}>{l}</Link>)}
          <Link to={dashHref} data-testid="nav-dashboard" className={linkCls}>{user ? "Dashboard" : "Login"}</Link>
          {user && (
            <button data-testid="nav-logout" onClick={() => { logout(); navigate("/"); }} className={linkCls}>Log out</button>
          )}
          <a href={BOOKING_URL} data-testid="nav-book-now" className="btn-light h-10 px-6">Book a Shoot</a>
        </div>

        <button data-testid="nav-mobile-toggle" aria-label="Menu" className={`xl:hidden ${text}`} onClick={() => setOpen(!open)}>
          {open ? <X strokeWidth={1.25} /> : <Menu strokeWidth={1.25} />}
        </button>
      </nav>

      {open && (
        <div data-testid="nav-mobile-drawer" className="xl:hidden bg-[#0B0C0E] border-t border-white/10 px-6 pt-10 pb-12 flex flex-col gap-7 min-h-[calc(100vh-96px)] text-white">
          {LINKS.map(([l, to, id]) => <Link key={to} to={to} data-testid={`mobile-${id}`} className="font-display text-5xl">{l}</Link>)}
          <Link to={dashHref} data-testid="nav-mobile-dashboard" className="font-display text-5xl">{user ? "Dashboard" : "Login"}</Link>
          {user && <button data-testid="nav-mobile-logout" onClick={() => { logout(); navigate("/"); }} className="text-left eyebrow">Log out</button>}
          <a href={BOOKING_URL} data-testid="nav-mobile-book-now" className="btn-dark mt-6">Book a Shoot</a>
        </div>
      )}
    </header>
  );
}
